import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { CHECKOUT } from '@/constants/testIds';

const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export const CheckoutSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { refresh } = useAuth();
  const [status, setStatus] = useState('AWAITING_STRIPE_CONFIRMATION');
  const [done, setDone] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('MISSING_SESSION_ID');
      setDone(true);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/session/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('PAYMENT_CONFIRMED');
          setDone(true);
          refresh();
          return;
        }
        if (data.status === 'expired') {
          setStatus('SESSION_EXPIRED');
          setDone(true);
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setStatus('CONFIRMATION_PENDING · check /pro/manage shortly');
          setDone(true);
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (attempts.current >= MAX_POLLS) {
          setStatus('STATUS_CHECK_FAILED');
          setDone(true);
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div
      data-testid={CHECKOUT.successContainer}
      className="max-w-xl mx-auto mt-20 px-6"
    >
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // POST_CHECKOUT
      </div>
      <h1 className="text-3xl font-bold uppercase tracking-tighter mb-8">
        Confirmation<span className="caelix-caret"></span>
      </h1>
      <div className="c-card">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">STATUS</div>
        <div
          data-testid={CHECKOUT.statusText}
          className={`text-lg font-mono ${
            status === 'PAYMENT_CONFIRMED'
              ? 'text-[#38BDF8]'
              : status.startsWith('AWAITING') || status.startsWith('CONFIRMATION')
              ? 'text-white'
              : 'text-yellow-400'
          }`}
        >
          {status}
          {!done && <span className="caelix-dots"></span>}
        </div>
        <div className="mt-6 pt-4 border-t border-[#3F3F46] flex flex-wrap gap-3">
          <Link
            to="/pro/manage"
            data-testid={CHECKOUT.goManageBtn}
            className="c-btn-primary text-xs"
          >
            OPEN_CONTROL_ROOM
          </Link>
          <Link to="/pricing" className="c-btn-secondary text-xs">
            BACK_TO_PRICING
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
